import { AccessForbiddenPage } from '@openx/components-core/lib/AccessForbiddenBackPage/AccessForbiddenPage.tsx';
import { PageHeader } from '@openx/components-header/PageHeader.tsx';
import { LalSegmentCreateForm } from '@openx/components-segment/lib/LalSegmentsForm/LalSegmentCreateForm.tsx';
import { LalSegmentEditForm } from '@openx/components-segment/lib/LalSegmentsForm/LalSegmentEditForm.tsx';
import { useSegmentContext } from '@openx/components-segment/lib/utils/context/SegmentContext.tsx';
import { isLalSegmentDeleted } from '@openx/components-segment/lib/utils/isLalSegmentDeleted.ts';
import { isLalSegmentInProcessing } from '@openx/components-segment/lib/utils/isLalSegmentInProcessing.ts';
import { useShowLal } from '@openx/components-segment/lib/utils/useShowLal.ts';
import { useCanGoBack } from '@openx/utils/hooks/useCanGoBack.ts';
import { type JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useAuth } from '../../../../components/AuthContext/context.ts';
import { isInternalAdmin } from '../../../../permissions/validators.ts';

export const LalFormPage = (): JSX.Element | null => {
  const { t } = useTranslation();

  const { lalSegmentUid = '' } = useParams();
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();

  const { session } = useAuth();
  const { useLalSegmentFetch } = useSegmentContext();

  const showLal = useShowLal();
  const accountUid = useMemo(() => session?.account.id ?? null, [session]);

  const { lalSegment, loading } = useLalSegmentFetch(lalSegmentUid);

  const handleGoBack = (fallbackRoute: string) => {
    if (canGoBack) {
      navigate(-1);
    } else {
      navigate(fallbackRoute);
    }
  };

  if (!accountUid) {
    return null;
  }

  if (!showLal) {
    return <Navigate to="/segments/audience" />;
  }

  if (!lalSegmentUid) {
    return (
      <PageHeader title={t('Create LAL Segment')} titlePrefix="OpenXSelect">
        <LalSegmentCreateForm accountUid={accountUid} goBack={() => handleGoBack('/segments/lookalike')} />
      </PageHeader>
    );
  }

  const isInternalOxAdmin = isInternalAdmin(session);
  const isEditForbidden = !loading && lalSegment?.account.id !== accountUid && !isInternalOxAdmin;

  const canNotEditLalSegment =
    !loading && lalSegment && (isLalSegmentDeleted(lalSegment) || isLalSegmentInProcessing(lalSegment));

  if (isEditForbidden) {
    return <AccessForbiddenPage />;
  }

  if (canNotEditLalSegment) {
    return <Navigate to="/segments/lookalike" />;
  }

  return (
    <LalSegmentEditForm
      lalSegmentId={lalSegmentUid}
      goBack={() => handleGoBack(`/segments/lookalike/${lalSegmentUid}`)}
    />
  );
};
